import React, { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import Button from "../../../ui/Button";
import Dropdown from "../../../ui/SelectDropdown";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { toast } from "react-toastify";
import { assignCouponToUsers } from "../../../../redux/features/CouponSlice";
import { fetchUsersAsync } from "../../../../redux/features/adminSlice";
import { AssignedUser } from "../../../../redux/features/types/couponListTypes";

interface AssignCouponModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedCoupon: any;
}

type BuyerOption = { label: string; value: string };

const prettifyCouponType = (raw: string): string =>
  raw
    ?.split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ") ?? "";

/** Mirrors the read-only `Coupon code` pattern used in the Edit modal. */
const ReadOnlyField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div>
    <label className="block text-sm font-medium mb-1 text-gray-700">
      {label}
    </label>
    <input
      type="text"
      readOnly
      value={value}
      className="w-full border rounded px-3 py-2 text-sm bg-gray-50 text-gray-600"
    />
  </div>
);

const AssignCouponModal: React.FC<AssignCouponModalProps> = ({
  open,
  onClose,
  onSave,
  selectedCoupon,
}) => {
  const dispatch = useAppDispatch();
  const { buyers } = useAppSelector((state: RootState) => state.admin);
  const { assigningCoupon } = useAppSelector(
    (state: RootState) => state.coupons
  );

  const [assignedUserId, setAssignedUserId] = useState("");
  const [selectedBuyer, setSelectedBuyer] = useState<BuyerOption | null>(null);
  const [error, setError] = useState("");

  const assignedUsers: AssignedUser[] = selectedCoupon?.assignedUsers ?? [];
  const alreadyAssigned = assignedUsers.length > 0;
  const existingAssignee = alreadyAssigned ? assignedUsers[0] : null;

  // Reset form whenever the modal opens (or the coupon under it changes).
  useEffect(() => {
    if (!open) return;
    setAssignedUserId("");
    setSelectedBuyer(null);
    setError("");
  }, [open, selectedCoupon?.id]);

  // Pre-fetch buyers on open so the dropdown isn't empty before the user types.
  // Skipped when re-assignment is blocked — nothing to pick.
  useEffect(() => {
    if (!open || alreadyAssigned) return;
    dispatch(
      fetchUsersAsync({
        page: 1,
        limit: 50,
        filter: "",
        userType: "BUYER",
      })
    );
  }, [open, alreadyAssigned, dispatch]);

  const handleSearchBuyers = (query: string) => {
    dispatch(
      fetchUsersAsync({
        page: 1,
        limit: 50,
        filter: query,
        userType: "BUYER",
      })
    );
  };

  /**
   * Build the dropdown options from the buyers slice, and pin the currently
   * selected buyer so its label survives subsequent search-driven refetches
   * (the selected id may no longer be in the latest result page).
   */
  const buyerOptions: BuyerOption[] = useMemo(() => {
    const rows: any[] = buyers?.data?.data ?? [];
    const mapped: BuyerOption[] = rows
      .filter((b) => b?.id)
      .map((b) => ({
        label:
          b.fullName ||
          `${b.firstName ?? ""} ${b.lastName ?? ""}`.trim() ||
          b.email ||
          b.id,
        value: b.id,
      }));

    if (
      selectedBuyer &&
      !mapped.some((o) => o.value === selectedBuyer.value)
    ) {
      return [selectedBuyer, ...mapped];
    }
    return mapped;
  }, [buyers, selectedBuyer]);

  const handleBuyerSelect = (value: string) => {
    const match = buyerOptions.find((o) => o.value === value);
    if (match) setSelectedBuyer(match);
    setAssignedUserId(value);
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (alreadyAssigned) return; // safety net — button is disabled too

    if (!assignedUserId) {
      setError("Please select a buyer to assign this coupon to.");
      return;
    }

    if (!selectedCoupon?.couponCode) {
      toast.error("Missing coupon code.");
      return;
    }

    const result = await dispatch(
      assignCouponToUsers({
        couponCode: selectedCoupon.couponCode,
        assignedUserId: [assignedUserId],
      })
    );

    if (assignCouponToUsers.fulfilled.match(result)) {
      const payload = result.payload;
      if (payload.alreadyAssigned?.length > 0) {
        // Defensive: shouldn't happen because we block re-assign client-side,
        // but if it ever does, don't claim a fresh assignment succeeded.
        toast.warn(
          payload.message ||
            "Selected user is already assigned to this coupon."
        );
      } else {
        toast.success(payload.message || "Coupon assigned successfully");
      }
      onClose();
      onSave();
    } else {
      toast.error((result.payload as string) || "Failed to assign coupon");
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Assign coupon"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="toolbar"
            variant="ghost"
          />
          <Button
            type="submit"
            label="Assign coupon"
            onClick={handleSubmit}
            isLoading={assigningCoupon}
            disabled={alreadyAssigned}
            layout="toolbar"
          />
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <ReadOnlyField
          label="Coupon code"
          value={selectedCoupon?.couponCode ?? ""}
        />

        <ReadOnlyField
          label="Coupon type"
          value={
            selectedCoupon?.couponType
              ? prettifyCouponType(selectedCoupon.couponType)
              : ""
          }
        />

        {alreadyAssigned ? (
          // Slim safety-net. Reachable today only via race conditions or
          // future entry points — the list page already hides the "Assign
          // user" action when there's an existing assignee.
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Info className="w-4 h-4 shrink-0" />
            <span>
              Already assigned to{" "}
              <span className="font-medium">
                {existingAssignee?.fullName ?? "a user"}
              </span>
            </span>
          </div>
        ) : (
          <Dropdown
            label="Buyer"
            name="assignedUserId"
            options={buyerOptions}
            placeholder="Search and select a buyer"
            value={assignedUserId}
            onSelect={handleBuyerSelect}
            onSearch={handleSearchBuyers}
            error={error}
            required
          />
        )}
      </form>
    </CustomModalContainer>
  );
};

export default AssignCouponModal;
